import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Star, ExternalLink, Clock } from "lucide-react";

export default function AdminPromotionsReview() {
  const [items, setItems] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [reason, setReason] = useState("");
  const [featured, setFeatured] = useState(false);

  const load = async () => setItems((await api.get("/admin/promotions/pending")).data);
  useEffect(() => { load(); }, []);

  const doReview = async (pid, action) => {
    try {
      await api.post(`/promotions/${pid}/review`, {
        action,
        reason: action === "reject" ? reason : "",
        featured: action === "approve" ? featured : false,
      });
      toast.success(action === "approve" ? "Aprovada" : "Rebutjada");
      setReviewing(null); setReason(""); setFeatured(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-promotions-review">
      <div>
        <div className="overline text-accent">Cua d'aprovació</div>
        <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Promocions públiques pendents</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Els associats han enviat aquestes promocions per publicar-les a la zona pública. Revisa-les i aprova o rebutja.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map(p => (
          <Card key={p.id} className="rounded-2xl border-2 overflow-hidden" data-testid={`review-card-${p.id}`}>
            {p.image_url && <div className="aspect-video overflow-hidden bg-secondary"><img src={p.image_url} alt="" className="w-full h-full object-cover" /></div>}
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <Badge className="bg-amber-500/15 text-amber-700 border-amber-300 mb-2"><Clock className="w-3 h-3 mr-1" /> Pendent</Badge>
                  <h3 className="font-heading font-semibold text-lg leading-tight">{p.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">Per <strong>{p.associate?.commercial_name || "—"}</strong> · {p.category}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-3">{p.description}</p>
              <div className="text-xs text-muted-foreground">
                📅 {p.start_date?.slice(0, 10)} — {p.end_date?.slice(0, 10)}
              </div>
              {p.conditions && <div className="text-xs bg-secondary/60 rounded p-2">💡 {p.conditions}</div>}
              <div className="flex gap-2 pt-2 border-t">
                <Button size="sm" onClick={() => { setReviewing(p); setFeatured(false); }} data-testid={`review-btn-${p.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Revisar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 text-muted-foreground">
            <CheckCircle2 className="w-12 h-12 mx-auto opacity-30 mb-3" />
            Cap promoció pendent. Tot al dia! ✨
          </div>
        )}
      </div>

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Revisar: {reviewing?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">Per {reviewing?.associate?.commercial_name}</div>
            <div className="p-3 rounded-xl bg-secondary/60 text-sm">{reviewing?.description}</div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} data-testid="review-featured" />
              <Star className="w-4 h-4 text-accent" /> Destacar a la portada
            </label>
            <div>
              <label className="text-sm font-medium">Motiu de rebuig (si aplica)</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Comunicaràs aquest motiu a l'associat" className="mt-2" data-testid="review-reason" />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="destructive" onClick={() => doReview(reviewing.id, "reject")} data-testid="review-reject-btn">
              <XCircle className="w-4 h-4 mr-2" /> Rebutjar
            </Button>
            <Button onClick={() => doReview(reviewing.id, "approve")} data-testid="review-approve-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
